'use client';
import { useMicrocopy } from '@contexts/MicrocopyContext';
import { AtomicContext } from '@contexts/coveo/AtomicContext';
import {
  loadAdvancedSearchQueryActions,
  loadFacetSetActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
} from '@coveo/atomic-react';
import { analytics } from '@lib/analytics/page-events';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FacetFilter } from 'ui-components';

const ACTIVE_FLASH_QUERY =
  '(@ec_sale_filters=="flash_deal" AND @ec_flash_promo_starts_at<=now)';
const UPCOMING_FLASH_QUERY =
  '(@ec_sale_filters=="flash_deal" AND @ec_flash_teaser_starts_at<=now AND @ec_flash_promo_starts_at>now)';
const ON_SALE_QUERY = '@ec_sale_filters=="on_sale"';
const NEW_RELEASE_QUERY = '@ec_sale_filters=="new_release_discount"';

type SaleFilterOption =
  'active_flash' | 'upcoming_flash' | 'on_sale' | 'new_release';

export const SaleFilter = ({
  context,
  isLoading = true,
}: {
  context: AtomicContext;
  isLoading?: boolean;
}) => {
  const { searchEngine } = useContext(context);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const labelsByKey = useMemo(() => {
    const src = searchLabelsMicrocopy || [];
    return Object.fromEntries(src.map((m) => [m.key, m.value]));
  }, [searchLabelsMicrocopy]);

  const valuesByKey = useMemo(() => {
    const src = searchValuesMicrocopy || [];
    return Object.fromEntries(src.map((m) => [m.key, m.value]));
  }, [searchValuesMicrocopy]);

  const saleFacetLabel = labelsByKey['search.filter-onsale-label'] || 'Sale';
  const filtersLabel = labelsByKey['search.filters'] || 'Filters';
  const clearLabel = valuesByKey['search.ui-values.clear'] || 'Clear';

  const advActions = useMemo(
    () => (searchEngine ? loadAdvancedSearchQueryActions(searchEngine) : null),
    [searchEngine]
  );
  const facetActions = useMemo(
    () => (searchEngine ? loadFacetSetActions(searchEngine) : null),
    [searchEngine]
  );
  const searchActions = useMemo(
    () => (searchEngine ? loadSearchActions(searchEngine) : null),
    [searchEngine]
  );
  const analyticsActions = useMemo(
    () => (searchEngine ? loadSearchAnalyticsActions(searchEngine) : null),
    [searchEngine]
  );

  const allowedValues: SaleFilterOption[] = useMemo(
    () => ['active_flash', 'upcoming_flash', 'on_sale', 'new_release'],
    []
  );
  const allowedValuesSet = useMemo(
    () => new Set<string>(allowedValues),
    [allowedValues]
  );

  const [selectedFilters, setSelectedFilters] = useState<Set<SaleFilterOption>>(
    new Set()
  );

  const [availableCoveoValues, setAvailableCoveoValues] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    if (!searchEngine || !facetActions) return;

    const state = searchEngine.state as any;
    if (!state?.facetSet?.['ec_sale_filters']) {
      const registerAction = (facetActions as any).registerFacet({
        facetId: 'ec_sale_filters',
        field: 'ec_sale_filters',
        numberOfValues: 10,
      });
      searchEngine.dispatch(registerAction);
    }
  }, [searchEngine, facetActions]);

  // Subscribe to Coveo state changes to get available facet values
  useEffect(() => {
    if (!searchEngine) return;

    const updateAvailableValues = () => {
      const state = searchEngine.state;
      const responseFacets = state?.search?.response?.facets;
      const saleFacet = responseFacets?.find(
        (f) => f.facetId === 'ec_sale_filters'
      );

      if (saleFacet?.values) {
        const available = new Set<string>(
          saleFacet.values
            .filter((v) => v.numberOfResults > 0)
            .map((v: any) => v.value)
        );
        setAvailableCoveoValues(available);
      }
    };

    // Defer initial check to avoid state update during render
    const timeoutId = setTimeout(updateAvailableValues, 0);

    const unsubscribe = searchEngine.subscribe(() => {
      // Defer subscription updates as well
      setTimeout(updateAvailableValues, 0);
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [searchEngine]);

  const updateEngineImmediate = useCallback(
    (newFilters: Set<SaleFilterOption>) => {
      if (
        !searchEngine ||
        !advActions ||
        !facetActions ||
        !searchActions ||
        !analyticsActions
      ) {
        return;
      }

      const parts: string[] = [];

      if (newFilters.has('active_flash')) {
        parts.push(ACTIVE_FLASH_QUERY);
      }
      if (newFilters.has('upcoming_flash')) {
        parts.push(UPCOMING_FLASH_QUERY);
      }
      if (newFilters.has('on_sale')) {
        parts.push(ON_SALE_QUERY);
      }
      if (newFilters.has('new_release')) {
        parts.push(NEW_RELEASE_QUERY);
      }

      const filterQuery = parts.length > 0 ? `(${parts.join(') OR (')})` : '';

      const advancedQueryAction = advActions.updateAdvancedSearchQueries({
        aq: filterQuery,
      });
      searchEngine.dispatch(advancedQueryAction);

      const analyticsAction = analyticsActions.logInterfaceChange();
      const searchAction = searchActions.executeSearch(analyticsAction);
      searchEngine.dispatch(searchAction);
    },
    [searchEngine, advActions, facetActions, searchActions, analyticsActions]
  );

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updateEngine = useCallback(
    (newFilters: Set<SaleFilterOption>) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateEngineImmediate(newFilters);

        const params = new URLSearchParams(searchParams?.toString());
        const selected = Array.from(newFilters);
        if (selected.length > 0) {
          params.set('sale_filters', selected.join(','));
          const legacySet = new Set<string>();
          if (
            selected.includes('active_flash') ||
            selected.includes('upcoming_flash')
          ) {
            legacySet.add('flash_deal');
          }
          if (selected.includes('on_sale')) legacySet.add('on_sale');
          if (selected.includes('new_release'))
            legacySet.add('new_release_discount');
          params.set('f-ec_sale_filters', Array.from(legacySet).join(','));
        } else {
          params.delete('sale_filters');
          params.delete('f-ec_sale_filters');
        }
        const hash = typeof window !== 'undefined' ? window.location.hash : '';
        router.replace(`${pathname}?${params.toString()}${hash}`, {
          scroll: false,
        });
      }, 1500);
    },
    [updateEngineImmediate, searchParams, router, pathname]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const initialFilters = useMemo(() => {
    const param = searchParams?.get('sale_filters');
    const legacy = searchParams?.get('f-ec_sale_filters');
    let values: string[] = [];
    if (param) {
      values = param.split(',').filter((v) => allowedValuesSet.has(v));
    } else if (legacy) {
      const legacyVals = legacy.split(',');
      legacyVals.forEach((lv) => {
        if (lv === 'flash_deal') {
          values.push('active_flash', 'upcoming_flash');
        } else if (lv === 'on_sale') {
          values.push('on_sale');
        } else if (lv === 'new_release_discount') {
          values.push('new_release');
        }
      });
    }
    const filtered = values.filter((v) => allowedValuesSet.has(v));
    return filtered.length ? { sale: filtered } : undefined;
  }, [searchParams, allowedValuesSet]);

  const handleFacetFiltersChange = useCallback(
    (filters: Record<string, string | string[]>) => {
      const raw = (filters['sale'] as string[]) || [];
      const valid = raw.filter((v): v is SaleFilterOption =>
        allowedValuesSet.has(v)
      );
      const next = new Set<SaleFilterOption>(valid);

      if (
        next.size === selectedFilters.size &&
        Array.from(next).every((v) => selectedFilters.has(v))
      ) {
        return;
      }

      // Push analytics events for selections
      Array.from(next).forEach((option) => {
        if (!selectedFilters.has(option)) {
          analytics.pushFilterEvent({
            filter_action: 'select',
            filter_action_category: 'ec_sale_filters',
            filter_action_label: option,
          });
        }
      });

      // Push analytics events for
      Array.from(selectedFilters).forEach((option) => {
        if (!next.has(option)) {
          analytics.pushFilterEvent({
            filter_action: 'deselect',
            filter_action_category: 'ec_sale_filters',
            filter_action_label: option,
          });
        }
      });

      setSelectedFilters(next);
      updateEngine(next);
    },
    [allowedValuesSet, selectedFilters, updateEngine]
  );

  const optionToCoveoValue: Record<SaleFilterOption, string> = useMemo(
    () => ({
      active_flash: 'flash_deal',
      upcoming_flash: 'flash_deal',
      new_release: 'new_release_discount',
      on_sale: 'on_sale',
    }),
    []
  );

  const facets = useMemo(() => {
    const allOptions = [
      {
        value: 'active_flash',
        label: valuesByKey['search.active_flash_deals'] || 'Active Flash Deals',
      },
      {
        value: 'upcoming_flash',
        label:
          valuesByKey['search.upcoming_flash_deals'] || 'Upcoming Flash Deals',
      },
      {
        value: 'new_release',
        label: valuesByKey['search.new_release'] || 'New release discount',
      },
      {
        value: 'on_sale',
        label: valuesByKey['search.on_sale'] || 'On sale',
      },
    ];

    const filteredOptions =
      availableCoveoValues.size === 0
        ? allOptions
        : allOptions.filter((option) => {
            const coveoValue =
              optionToCoveoValue[option.value as SaleFilterOption];
            return availableCoveoValues.has(coveoValue);
          });

    return [
      {
        id: 'sale',
        label: saleFacetLabel,
        type: 'multiple' as const,
        displayCount: true,
        options: filteredOptions,
      },
    ];
  }, [saleFacetLabel, valuesByKey, availableCoveoValues, optionToCoveoValue]);

  const microcopy = useMemo(
    () => ({
      label: filtersLabel,
      description: '',
      clearLabel,
      removeLabel: 'Remove',
      filterNoun: 'filter',
    }),
    [filtersLabel, clearLabel]
  );

  const hasOptions = facets[0]?.options.length > 0;
  if (!hasOptions) return null;

  return (
    <FacetFilter
      isLoading={isLoading}
      facets={facets}
      initialFilters={initialFilters}
      onFiltersChange={handleFacetFiltersChange}
      hideSelectedBadges
      hideFiltersLabel
      microcopy={microcopy}
    />
  );
};
